// frontend/src/components/TakenWidget.js
// Dashboard widget die projekt-taken toont, gesorteerd op deadline-urgentie.
//
// Integratie:
//   import TakenWidget from '../components/TakenWidget';
//   Ergens in Dashboard.js tussen de widgets plaatsen:
//     <TakenWidget />

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

const formatDatum = (d) => {
  if (!d) return '';
  return new Date(d).toLocaleDateString('nl-NL', { day: '2-digit', month: 'short' });
};

const TaakItem = ({ taak, kleur, onClick }) => (
  <div
    onClick={onClick}
    style={{
      padding: '10px 12px',
      borderLeft: `3px solid ${kleur}`,
      background: '#fff',
      marginBottom: 6,
      borderRadius: 4,
      cursor: 'pointer',
      boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
      transition: 'transform 0.1s'
    }}
    onMouseEnter={(e) => (e.currentTarget.style.transform = 'translateX(2px)')}
    onMouseLeave={(e) => (e.currentTarget.style.transform = 'translateX(0)')}
  >
    <div style={{ fontWeight: 600, fontSize: 14, color: '#1e293b', marginBottom: 2 }}>
      {taak.titel}
    </div>
    <div style={{ fontSize: 12, color: '#64748b', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
      <span>📁 {taak.project_code || taak.project_naam}</span>
      {taak.klant_naam && <span>• 👤 {taak.klant_naam}</span>}
      {taak.toegewezen_aan_naam && <span>• 🧑‍💼 {taak.toegewezen_aan_naam}</span>}
      {taak.deadline && (
        <span style={{ color: kleur, fontWeight: 600 }}>
          • 📅 {formatDatum(taak.deadline)}
          {taak.dagen_tot_deadline !== null && taak.dagen_tot_deadline < 0 &&
            ` (${Math.abs(taak.dagen_tot_deadline)} dag${Math.abs(taak.dagen_tot_deadline) === 1 ? '' : 'en'} te laat)`}
        </span>
      )}
    </div>
  </div>
);

const Sectie = ({ titel, taken, kleur, icoon, onTaakClick }) => {
  if (!taken || taken.length === 0) return null;
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{
        fontSize: 12,
        fontWeight: 700,
        color: kleur,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
        marginBottom: 6,
        display: 'flex',
        alignItems: 'center',
        gap: 6
      }}>
        <span>{icoon}</span>
        <span>{titel}</span>
        <span style={{
          background: kleur,
          color: 'white',
          borderRadius: 10,
          padding: '1px 8px',
          fontSize: 11
        }}>
          {taken.length}
        </span>
      </div>
      {taken.map(t => (
        <TaakItem key={t.id} taak={t} kleur={kleur} onClick={() => onTaakClick(t)} />
      ))}
    </div>
  );
};

const TakenWidget = () => {
  const navigate = useNavigate();
  const [groepen, setGroepen] = useState(null);
  const [totaal, setTotaal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let actief = true;
    const load = async () => {
      try {
        const res = await api.get('/projecten/taken/dashboard');
        if (!actief) return;
        setGroepen(res.data.groepen || {});
        setTotaal(res.data.totaal || 0);
        setError(null);
      } catch (err) {
        if (!actief) return;
        console.error('Taken widget fout:', err);
        setError('Kon taken niet laden');
      } finally {
        if (actief) setLoading(false);
      }
    };

    load();
    const interval = setInterval(load, 60000); // ververs elke minuut
    return () => { actief = false; clearInterval(interval); };
  }, []);

  const handleTaakClick = (taak) => {
    navigate(`/projecten/${taak.project_id}`);
  };

  if (loading) {
    return (
      <div className="widget">
        <div className="widget-header">
          <h3>✅ Mijn Taken</h3>
        </div>
        <div className="widget-content">
          <div className="widget-empty">Laden...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="widget">
      <div className="widget-header">
        <h3>✅ Mijn Taken</h3>
        <span className="widget-count">{totaal}</span>
      </div>
      <div className="widget-content">
        {error && <div className="widget-empty" style={{ color: '#dc2626' }}>{error}</div>}

        {!error && totaal === 0 && (
          <div className="widget-empty">🎉 Geen openstaande taken!</div>
        )}

        {!error && groepen && (
          <>
            <Sectie
              titel="Verlopen"
              icoon="🔴"
              taken={groepen.verlopen}
              kleur="#dc2626"
              onTaakClick={handleTaakClick}
            />
            <Sectie
              titel="Vandaag"
              icoon="🟠"
              taken={groepen.vandaag}
              kleur="#ea580c"
              onTaakClick={handleTaakClick}
            />
            <Sectie
              titel="Deze week"
              icoon="🟡"
              taken={groepen.deze_week}
              kleur="#ca8a04"
              onTaakClick={handleTaakClick}
            />
            <Sectie
              titel="Later"
              icoon="🟢"
              taken={groepen.later}
              kleur="#16a34a"
              onTaakClick={handleTaakClick}
            />
            <Sectie
              titel="Geen deadline"
              icoon="⚪"
              taken={groepen.geen_deadline}
              kleur="#64748b"
              onTaakClick={handleTaakClick}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default TakenWidget;
